import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Alert,
  Dimensions,
  TouchableOpacity,
  Animated
} from 'react-native';
import {
  Text,
  TextInput,
  Button,
  Card,
  Title,
  Paragraph,
  Chip,
  ProgressBar,
  Surface
} from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { getUserProfile, chineseZodiac, westernZodiac, numerology, calculateWesternAspectCompatibility, calculateChineseCompatibility } from '../../utils/compatibility';
import { getLifePathDisplayValue, shouldShowAlternateLifePath } from '../../utils/numerology';
import { 
  colors, 
  spacing, 
  borderRadius, 
  shadows, 
  typography,
  commonStyles,
  getCardMaxWidth,
  getPadding 
} from './sharedStyles';

export default function CompareCalculatorComponent({ prefilledUser }) {
  const [person1BirthDate, setPerson1BirthDate] = useState('');
  const [person2BirthDate, setPerson2BirthDate] = useState('');
  const [results, setResults] = useState(null);

  const { width } = Dimensions.get('window');
  const isDesktop = width >= 1024;
  const isTablet = width >= 768 && width < 1024;
  const isMobile = width < 768;

  useEffect(() => {
    if (prefilledUser?.birthDate) {
      setPerson2BirthDate(prefilledUser.birthDate);
    }
  }, [prefilledUser?.birthDate]);

  const formatBirthDate = (text) => {
    const numbers = text.replace(/\D/g, '');
    
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 4)}-${numbers.slice(4, 8)}`;
    }
  };

  const handleBirthDateChange = (text, personNumber) => {
    const formatted = formatBirthDate(text);
    if (personNumber === 1) {
      setPerson1BirthDate(formatted);
    } else {
      setPerson2BirthDate(formatted);
    }
  };

  const renderLifePathCombinationBreakdown = (lifePathData) => {
    const combinations = lifePathData?.combinations || [];
    const birthdayComposite = lifePathData?.birthdayComposite;
    if (!combinations.length && !birthdayComposite) return null;

    return (
      <View style={styles.lifePathBreakdown}>
        {combinations.map((combination) => (
          <View key={combination.key} style={styles.lifePathBreakdownItem}>
            <Text style={styles.lifePathBreakdownLabel}>
              {combination.label}
            </Text>
            <Text style={styles.lifePathBreakdownValue}>
              Classical {combination.classical.reduction}
            </Text>
            <Text style={styles.lifePathBreakdownValue}>
              Reduced {combination.reduced.reduction}
            </Text>
          </View>
        ))}
        {birthdayComposite && (
          <View style={styles.lifePathComposite}>
            <Text style={styles.lifePathBreakdownLabel}>
              {birthdayComposite.label}
            </Text>
            <Text style={styles.lifePathBreakdownValue}>
              Month + Day {birthdayComposite.monthDay.reduction}
            </Text>
            <Text style={styles.lifePathBreakdownValue}>
              Day + Year {birthdayComposite.dayYear.reduction}
            </Text>
            <Text style={styles.lifePathCompositeValue}>
              Total {birthdayComposite.total.reduction}
            </Text>
          </View>
        )}
      </View>
    );
  };

  const calculateCompatibilityWithData = (birthDate1, birthDate2) => {
    if (!birthDate1 || birthDate1.length < 10 || !birthDate2 || birthDate2.length < 10) {
      Alert.alert('Error', 'Please enter valid birth dates for both people');
      return;
    }

    const [month1, day1, year1] = birthDate1.split('-');
    const [month2, day2, year2] = birthDate2.split('-');
    const isoDateString1 = `${year1}-${month1}-${day1}`;
    const isoDateString2 = `${year2}-${month2}-${day2}`;
    
    const profile1 = getUserProfile('Person 1', isoDateString1);
    const profile2 = getUserProfile(prefilledUser?.name || 'Person 2', isoDateString2);
    
    const chineseScore = calculateChineseCompatibility(profile1, profile2);
    const westernScore = calculateWesternCompatibility(profile1, profile2);
    const numerologyScore = calculateNumerologyCompatibility(profile1, profile2);
    
    const overallScore = Math.round((chineseScore.score + westernScore.score + numerologyScore.score) / 3);
    
    setResults({
      profile1,
      profile2,
      overall: overallScore,
      chinese: chineseScore,
      western: westernScore,
      numerology: numerologyScore
    });
  };
  
  const calculateCompatibility = () => {
    calculateCompatibilityWithData(person1BirthDate, person2BirthDate);
  };
  
  const handleReset = () => {
    setPerson1BirthDate('');
    setPerson2BirthDate('');
    setResults(null);
  };

  // Chinese compatibility delegated to unified module via utils

  const calculateWesternCompatibility = (profile1, profile2) => {
    const a = profile1.westernSign.name;
    const b = profile2.westernSign.name;
    const { score, description } = calculateWesternAspectCompatibility(a, b);
    return { score, description };
  };

  const calculateNumerologyCompatibility = (profile1, profile2) => {
    // Import the shared numerology module function
    const { calculateLifePathCompatibility } = require('../../utils/numerology');
    const result = calculateLifePathCompatibility(profile1.lifePath, profile2.lifePath);
    return { score: result.score, description: result.description };
  };

  const getScoreColor = (score) => {
    if (score >= 85) return '#4CAF50';
    if (score >= 70) return '#FF9800';
    if (score >= 55) return '#2196F3';
    return '#9C27B0';
  };
  
  const getCompatibilityDescription = (score) => {
    if (score >= 85) return '🔥 Exceptional cosmic alignment! You two are practically written in the stars.';
    if (score >= 70) return '✨ Strong compatibility with great potential for lasting connection.';
    if (score >= 55) return '🌟 Good compatibility with room for beautiful growth together.';
    return '💫 Unique cosmic connection that requires understanding and patience.';
  };

  return (
    <View style={commonStyles.container}>
      <ScrollView style={styles.scrollView}>
        <View style={styles.mainContent}>
          {/* Input Section */}
          <Surface style={[commonStyles.solidCard, styles.inputCard]}>
            {/* Header Section */}
            <View style={styles.headerContainer}>
              <LinearGradient
                colors={['rgba(63, 81, 181, 0.1)', 'rgba(121, 134, 203, 0.1)']}
                style={styles.headerIconContainer}
              >
                <Ionicons name="people" size={32} color={colors.primary} />
              </LinearGradient>
              <Title style={styles.headerTitle}>Compatibility Comparison</Title>
              <Text style={styles.headerSubtitle}>
                Compare birth dates to discover your cosmic compatibility
              </Text>
            </View>
            <View style={styles.inputSection}>
              <View style={styles.personInputContainer}>
                <View style={styles.personHeader}>
                  <LinearGradient
                    colors={[colors.success + '15', colors.success + '25']}
                    style={styles.personIcon}
                  >
                    <Ionicons name="person" size={22} color={colors.success} />
                  </LinearGradient>
                  <Text style={styles.personLabel}>
                    Person 1 Birth Date
                  </Text>
                </View>
                <Surface style={styles.inputWrapper}>
                  <View style={styles.inputWithIcon}>
                    <Ionicons 
                      name="calendar" 
                      size={20} 
                      color={colors.primary} 
                      style={styles.inputIcon} 
                    />
                    <TextInput
                      value={person1BirthDate}
                      onChangeText={(text) => handleBirthDateChange(text, 1)}
                      selectTextOnFocus={true}
                      style={styles.modernInput}
                      mode="outlined"
                      placeholder="MM-DD-YYYY"
                      placeholderTextColor={colors.textSecondary + '80'}
                      keyboardType="numeric"
                      maxLength={10}
                      theme={{
                        colors: {
                          primary: colors.primary,
                          outline: colors.primary + '30',
                        },
                      }}
                      outlineColor="transparent"
                      activeOutlineColor={colors.primary}
                    />
                  </View>
                </Surface>
              </View>

              <View style={styles.vsContainer}>
                <Surface style={styles.vsCircleContainer}>
                  <LinearGradient
                    colors={[colors.primary + '10', colors.primaryLight + '20']}
                    style={styles.vsCircle}
                  >
                    <Ionicons name="heart" size={18} color={colors.primary} />
                    <Text style={styles.vsText}>VS</Text>
                  </LinearGradient>
                </Surface>
              </View>

              <View style={styles.personInputContainer}>
                <View style={styles.personHeader}>
                  <LinearGradient
                    colors={[colors.info + '15', colors.info + '25']}
                    style={styles.personIcon}
                  >
                    <Ionicons name="person-outline" size={22} color={colors.info} />
                  </LinearGradient>
                  <Text style={styles.personLabel}>
                    {prefilledUser?.name ? `${prefilledUser.name.split(' ')[0]}'s` : 'Person 2'} Birth Date
                  </Text>
                </View>
                <Surface style={styles.inputWrapper}>
                  <View style={styles.inputWithIcon}>
                    <Ionicons 
                      name="calendar" 
                      size={20} 
                      color={colors.primary} 
                      style={styles.inputIcon} 
                    />
                    <TextInput
                      value={person2BirthDate}
                      onChangeText={(text) => handleBirthDateChange(text, 2)}
                      selectTextOnFocus={true}
                      style={styles.modernInput}
                      mode="outlined"
                      placeholder="MM-DD-YYYY"
                      placeholderTextColor={colors.textSecondary + '80'}
                      keyboardType="numeric"
                      maxLength={10}
                      theme={{
                        colors: {
                          primary: colors.primary,
                          outline: colors.primary + '30',
                        },
                      }}
                      outlineColor="transparent"
                      activeOutlineColor={colors.primary}
                    />
                  </View>
                </Surface>
              </View>
            </View>

            <View style={styles.buttonContainer}>
              <LinearGradient
                colors={[colors.primary, colors.primaryLight]}
                style={styles.gradientButton}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
              >
                <Button
                  mode="contained"
                  onPress={calculateCompatibility}
                  style={styles.calculateButton}
                  labelStyle={styles.calculateButtonText}
                  contentStyle={styles.calculateButtonContent}
                  buttonColor="transparent"
                >
                  <Ionicons name="analytics" size={18} color={colors.white} style={styles.buttonIcon} />
                  Compare Compatibility
                </Button>
              </LinearGradient>
              
              {results && (
                <Button
                  mode="outlined"
                  onPress={handleReset}
                  style={styles.resetButton}
                  labelStyle={styles.resetButtonText}
                >
                  <Ionicons name="refresh" size={16} color={colors.primary} />
                  Reset
                </Button>
              )}
            </View>
          </Surface>

          {results && (
            <View style={styles.resultsContainer}>
              <Surface style={[commonStyles.resultCard, styles.overallCard]}>
                <View style={styles.overallHeader}>
                  <LinearGradient
                    colors={['rgba(116, 75, 162, 0.1)', 'rgba(102, 126, 234, 0.1)']}
                    style={styles.overallIconContainer}
                  >
                    <Ionicons name="heart-circle" size={32} color={colors.primary} />
                  </LinearGradient>
                  <Title style={styles.overallTitle}>
                    Overall Compatibility Score
                  </Title>
                  <Text style={styles.overallSubtitle}>
                    Based on comprehensive cosmic analysis
                  </Text>
                </View>

                <View style={styles.scoreDisplayContainer}>
                  <Surface style={[styles.modernScoreCircle, { borderColor: getScoreColor(results.overall) + '40' }]}>
                    <LinearGradient
                      colors={[getScoreColor(results.overall) + '20', getScoreColor(results.overall) + '10']}
                      style={styles.scoreCircleGradient}
                    >
                      <Text style={[styles.modernScoreText, { color: getScoreColor(results.overall) }]}>
                        {results.overall}%
                      </Text>
                      <Text style={styles.scoreLabel}>Compatibility</Text>
                    </LinearGradient>
                  </Surface>
                </View>

                <View style={styles.descriptionContainer}>
                  <Text style={styles.compatibilityDescription}>
                    {getCompatibilityDescription(results.overall)}
                  </Text>
                </View>
              </Surface>

              <View style={styles.profilesContainer}>
                <Surface style={[styles.modernProfileCard, styles.profileCard1]}>
                  <View style={styles.profileHeader}>
                    <View style={styles.profileIconCircle}>
                      <Ionicons name="person" size={24} color={colors.primary} />
                    </View>
                    <Title style={styles.modernProfileName}>
                      Person 1
                    </Title>
                  </View>
                  <View style={styles.profileChipsContainer}>
                    <View style={styles.profileChipRow}>
                      <Surface style={[styles.modernProfileChip, styles.westernChip]}>
                        <Text style={styles.chipEmoji}>⭐</Text>
                        <Text style={styles.chipLabel}>Western</Text>
                        <Text style={styles.chipValue}>{results.profile1.westernSign.name}</Text>
                      </Surface>
                      <Surface style={[styles.modernProfileChip, styles.chineseChip]}>
                        <Text style={styles.chipEmoji}>🐉</Text>
                        <Text style={styles.chipLabel}>Chinese</Text>
                        <Text style={styles.chipValue}>{results.profile1.chineseAnimal}</Text>
                      </Surface>
                    </View>
                    <Surface style={[styles.modernProfileChip, styles.numerologyChip, styles.fullWidthChip, [11, 22, 28, 33].includes(results.profile1.lifePath) && styles.masterNumberChip]}>
                      <Text style={styles.chipEmoji}>🔢</Text>
                      <Text style={styles.chipLabel}>Life Path</Text>
                      <Text style={[styles.chipValue, styles.largeChipValue, [11, 22, 28, 33].includes(results.profile1.lifePath) && styles.masterNumberValue]}>
                        {getLifePathDisplayValue(results.profile1.lifePathData, results.profile1.lifePath)}
                      </Text>
                      {[11, 22, 28, 33].includes(results.profile1.lifePath) && (
                        <Text style={styles.masterNumberLabel}>
                          {results.profile1.lifePathData && results.profile1.lifePathData.isPure ? 'Pure Master Number' : 'Master Number'}
                          {results.profile1.lifePathData && !results.profile1.lifePathData.isPure && results.profile1.lifePathData.reduction.includes('/') &&
                            ` (${results.profile1.lifePathData.reduction.split('/').slice(-1)[0]})`
                          }
                        </Text>
                      )}
                      {shouldShowAlternateLifePath(results.profile1.lifePathData) && (
                        <Text style={styles.masterNumberLabel}>
                          Alt method: {getLifePathDisplayValue(results.profile1.lifePathData.alternate)}
                        </Text>
                      )}
                      {renderLifePathCombinationBreakdown(results.profile1.lifePathData)}
                    </Surface>
                  </View>
                </Surface>

                <View style={[styles.vsContainer, isMobile && styles.vsContainerFullWidth]}>
                  <LinearGradient
                    colors={[colors.primary + '20', colors.primaryLight + '20']}
                    style={styles.heartContainer}
                  >
                    <Ionicons name="heart" size={28} color={colors.white} />
                  </LinearGradient>
                </View>

                <Surface style={[styles.modernProfileCard, styles.profileCard2]}>
                  <View style={styles.profileHeader}>
                    <View style={styles.profileIconCircle}>
                      <Ionicons name="person-outline" size={24} color={colors.primary} />
                    </View>
                    <Title style={styles.modernProfileName}>
                      Person 2
                    </Title>
                  </View>
                  <View style={styles.profileChipsContainer}>
                    <View style={styles.profileChipRow}>
                      <Surface style={[styles.modernProfileChip, styles.westernChip]}>
                        <Text style={styles.chipEmoji}>⭐</Text>
                        <Text style={styles.chipLabel}>Western</Text>
                        <Text style={styles.chipValue}>{results.profile2.westernSign.name}</Text>
                      </Surface>
                      <Surface style={[styles.modernProfileChip, styles.chineseChip]}>
                        <Text style={styles.chipEmoji}>🐉</Text>
                        <Text style={styles.chipLabel}>Chinese</Text>
                        <Text style={styles.chipValue}>{results.profile2.chineseAnimal}</Text>
                      </Surface>
                    </View>
                    <Surface style={[styles.modernProfileChip, styles.numerologyChip, styles.fullWidthChip, [11, 22, 28, 33].includes(results.profile2.lifePath) && styles.masterNumberChip]}>
                      <Text style={styles.chipEmoji}>🔢</Text>
                      <Text style={styles.chipLabel}>Life Path</Text>
                      <Text style={[styles.chipValue, styles.largeChipValue, [11, 22, 28, 33].includes(results.profile2.lifePath) && styles.masterNumberValue]}>
                        {getLifePathDisplayValue(results.profile2.lifePathData, results.profile2.lifePath)}
                      </Text>
                      {[11, 22, 28, 33].includes(results.profile2.lifePath) && (
                        <Text style={styles.masterNumberLabel}>
                          {results.profile2.lifePathData && results.profile2.lifePathData.isPure ? 'Pure Master Number' : 'Master Number'}
                          {results.profile2.lifePathData && !results.profile2.lifePathData.isPure && results.profile2.lifePathData.reduction.includes('/') &&
                            ` (${results.profile2.lifePathData.reduction.split('/').slice(-1)[0]})`
                          }
                        </Text>
                      )}
                      {shouldShowAlternateLifePath(results.profile2.lifePathData) && (
                        <Text style={styles.masterNumberLabel}>
                          Alt method: {getLifePathDisplayValue(results.profile2.lifePathData.alternate)}
                        </Text>
                      )}
                      {renderLifePathCombinationBreakdown(results.profile2.lifePathData)}
                    </Surface>
                  </View>
                </Surface>
              </View>

              <View style={[styles.detailsContainer, isDesktop && styles.desktopDetailsContainer]}>
                <Surface style={[styles.modernDetailCard, isDesktop && styles.desktopDetailCard]}>
                  <View style={styles.detailCardHeader}>
                    <LinearGradient
                      colors={['rgba(255, 215, 0, 0.1)', 'rgba(255, 215, 0, 0.05)']}
                      style={styles.detailIconContainer}
                    >
                      <Ionicons name="star" size={28} color={colors.warning} />
                    </LinearGradient>
                    <View style={styles.detailHeaderText}>
                      <Title style={styles.modernDetailTitle}>Western Astrology</Title>
                      <Text style={styles.detailSubtitle}>Birth month energy compatibility</Text>
                    </View>
                  </View>
                  
                  <View style={styles.progressSection}>
                    <Surface style={styles.progressContainer}>
                      <LinearGradient
                        colors={[getScoreColor(results.western.score) + '20', getScoreColor(results.western.score) + '40']}
                        style={[styles.progressBar, { width: `${results.western.score}%` }]}
                      />
                    </Surface>
                    <Text style={[styles.modernScoreLabel, { color: getScoreColor(results.western.score) }]}>
                      {results.western.score}%
                    </Text>
                  </View>
                  
                  <Text style={styles.modernDetailDescription}>
                    {results.western.description}
                  </Text>
                </Surface>

                <Surface style={[styles.modernDetailCard, isDesktop && styles.desktopDetailCard]}>
                  <View style={styles.detailCardHeader}>
                    <LinearGradient
                      colors={['rgba(255, 107, 107, 0.1)', 'rgba(255, 107, 107, 0.05)']}
                      style={styles.detailIconContainer}
                    >
                      <Ionicons name="paw" size={28} color={colors.error} />
                    </LinearGradient>
                    <View style={styles.detailHeaderText}>
                      <Title style={styles.modernDetailTitle}>Chinese Zodiac</Title>
                      <Text style={styles.detailSubtitle}>Birth year energy compatibility</Text>
                    </View>
                  </View>
                  
                  <View style={styles.progressSection}>
                    <Surface style={styles.progressContainer}>
                      <LinearGradient
                        colors={[getScoreColor(results.chinese.score) + '20', getScoreColor(results.chinese.score) + '40']}
                        style={[styles.progressBar, { width: `${results.chinese.score}%` }]}
                      />
                    </Surface>
                    <Text style={[styles.modernScoreLabel, { color: getScoreColor(results.chinese.score) }]}>
                      {results.chinese.score}%
                    </Text>
                  </View>
                  
                  <Text style={styles.modernDetailDescription}>
                    {results.chinese.description}
                  </Text>
                </Surface>

                <Surface style={[styles.modernDetailCard, isDesktop && styles.desktopDetailCard]}>
                  <View style={styles.detailCardHeader}>
                    <LinearGradient
                      colors={['rgba(108, 92, 231, 0.1)', 'rgba(108, 92, 231, 0.05)']}
                      style={styles.detailIconContainer}
                    >
                      <Ionicons name="infinite" size={28} color={colors.purple} />
                    </LinearGradient>
                    <View style={styles.detailHeaderText}>
                      <Title style={styles.modernDetailTitle}>Numerology</Title>
                      <Text style={styles.detailSubtitle}>Life Path energy compatibility</Text>
                    </View>
                  </View>
                  
                  <View style={styles.progressSection}>
                    <Surface style={styles.progressContainer}>
                      <LinearGradient
                        colors={[getScoreColor(results.numerology.score) + '20', getScoreColor(results.numerology.score) + '40']}
                        style={[styles.progressBar, { width: `${results.numerology.score}%` }]}
                      />
                    </Surface>
                    <Text style={[styles.modernScoreLabel, { color: getScoreColor(results.numerology.score) }]}>
                      {results.numerology.score}%
                    </Text>
                  </View>
                  
                  <Text style={styles.modernDetailDescription}>
                    {results.numerology.description}
                  </Text>
                </Surface>
              </View>
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    padding: spacing.lg,
  },
  mainContent: {
    alignItems: 'center',
    paddingBottom: spacing.xxl,
  },
  
  // Input Section
  inputCard: {
    width: '100%',
    maxWidth: getCardMaxWidth(800),
    alignSelf: 'center',
    marginBottom: spacing.xxl,
    padding: spacing.xxl,
  },
  
  // Header Section
  headerContainer: {
    alignItems: 'center',
    marginBottom: spacing.xxl,
  },
  headerIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  headerTitle: {
    ...typography.heading1,
    color: colors.primary,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  headerSubtitle: {
    ...typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
    maxWidth: 300,
    lineHeight: 22,
  },
  inputSection: {
    gap: spacing.lg,
  },
  personInputContainer: {
    flex: 1,
    gap: spacing.md,
  },
  personHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  personIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    ...shadows.input,
  },
  personLabel: {
    ...typography.bodySmall,
    color: colors.primary,
    fontWeight: '600',
  },
  inputWrapper: {
    backgroundColor: colors.white,
    borderRadius: borderRadius.lg,
    ...shadows.input,
    overflow: 'hidden',
  },
  inputWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
    paddingVertical: spacing.xs,
  },
  inputIcon: {
    position: 'absolute',
    left: spacing.lg,
    zIndex: 1,
  },
  modernInput: {
    flex: 1,
    backgroundColor: 'transparent',
    paddingLeft: spacing.xxxl + spacing.sm,
    elevation: 0,
    shadowOpacity: 0,
  },
  vsContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  vsContainerFullWidth: {
    width: '100%',
    paddingVertical: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  vsCircleContainer: {
    borderRadius: 35,
    ...shadows.button,
    overflow: 'hidden',
  },
  vsCircle: {
    width: 70,
    height: 70,
    borderRadius: 35,
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.xs,
  },
  vsText: {
    ...typography.caption,
    color: colors.primary,
    fontWeight: '800',
    letterSpacing: 1,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.lg,
    marginTop: spacing.xl,
  },
  gradientButton: {
    borderRadius: borderRadius.xl,
    ...shadows.button,
    flex: 1,
    maxWidth: 300,
  },
  calculateButton: {
    backgroundColor: 'transparent',
    elevation: 0,
    shadowOpacity: 0,
  },
  calculateButtonContent: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  calculateButtonText: {
    ...typography.button,
    color: colors.white,
  },
  buttonIcon: {
    marginRight: spacing.xs,
  },
  resetButton: {
    borderColor: colors.primary,
    borderRadius: borderRadius.xl,
    backgroundColor: 'transparent',
  },
  resetButtonText: {
    ...typography.bodySmall,
    color: colors.primary,
    fontWeight: '600',
  },
  // Results Section
  resultsContainer: {
    width: '100%',
    maxWidth: 900,
    alignSelf: 'center',
    gap: spacing.xxl,
  },
  overallCard: {
    width: '100%',
    alignSelf: 'center',
  },
  overallHeader: {
    alignItems: 'center',
    marginBottom: spacing.xxl,
  },
  overallIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  overallTitle: {
    ...typography.heading2,
    color: colors.primary,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  overallSubtitle: {
    ...typography.bodySmall,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  scoreDisplayContainer: {
    alignItems: 'center',
    marginBottom: spacing.xxl,
  },
  modernScoreCircle: {
    width: 140,
    height: 140,
    borderRadius: 70,
    borderWidth: 3,
    overflow: 'hidden',
    ...shadows.result,
  },
  scoreCircleGradient: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modernScoreText: {
    ...typography.heading1,
    fontSize: 36,
    fontWeight: '800',
    marginBottom: spacing.xs,
  },
  scoreLabel: {
    ...typography.caption,
    color: colors.textSecondary,
    fontWeight: '600',
  },
  descriptionContainer: {
    alignItems: 'center',
  },
  compatibilityDescription: {
    ...typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 24,
    maxWidth: 300,
  },
  // Profile Cards Section
  profilesContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.lg,
    flexWrap: 'wrap',
  },
  modernProfileCard: {
    flex: 1,
    minWidth: 280,
    backgroundColor: colors.background,
    borderRadius: borderRadius.xl,
    padding: spacing.xl,
    ...shadows.card,
    borderWidth: 1,
    borderColor: 'rgba(116, 75, 162, 0.1)',
  },
  profileCard1: {
    borderLeftWidth: 4,
    borderLeftColor: colors.success,
  },
  profileCard2: {
    borderLeftWidth: 4,
    borderLeftColor: colors.info,
  },
  profileHeader: {
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  profileIconCircle: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: colors.primary + '15',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  modernProfileName: {
    ...typography.heading3,
    color: colors.primary,
    textAlign: 'center',
  },
  profileChipsContainer: {
    gap: spacing.md,
  },
  profileChipRow: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  modernProfileChip: {
    flex: 1,
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    backgroundColor: colors.white,
    alignItems: 'center',
    gap: spacing.xs,
    ...shadows.input,
  },
  fullWidthChip: {
    flex: undefined,
    width: '100%',
  },
  chipEmoji: {
    fontSize: 20,
  },
  chipLabel: {
    ...typography.caption,
    color: colors.textSecondary,
    fontWeight: '600',
    textAlign: 'center',
  },
  chipValue: {
    ...typography.bodySmall,
    color: colors.primary,
    fontWeight: '700',
    textAlign: 'center',
  },
  largeChipValue: {
    ...typography.heading3,
    color: colors.primary,
  },
  chineseChip: {
    borderLeftWidth: 3,
    borderLeftColor: colors.warning,
  },
  westernChip: {
    borderLeftWidth: 3,
    borderLeftColor: colors.info,
  },
  numerologyChip: {
    borderLeftWidth: 3,
    borderLeftColor: colors.success,
  },
  masterNumberChip: {
    borderLeftColor: '#FFD700',
    backgroundColor: 'rgba(255, 215, 0, 0.05)',
    borderWidth: 2,
    borderColor: '#FFD700',
  },
  masterNumberValue: {
    color: '#FF8C00',
  },
  masterNumberLabel: {
    ...typography.caption,
    color: '#FF8C00',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: spacing.xs,
    textAlign: 'center',
  },
  lifePathBreakdown: {
    width: '100%',
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: 'rgba(76, 175, 80, 0.22)',
    gap: spacing.xs,
  },
  lifePathBreakdownItem: {
    width: '100%',
    gap: 2,
  },
  lifePathComposite: {
    width: '100%',
    gap: 2,
    marginTop: spacing.xs,
    paddingTop: spacing.xs,
    borderTopWidth: 1,
    borderTopColor: 'rgba(76, 175, 80, 0.16)',
  },
  lifePathBreakdownLabel: {
    ...typography.caption,
    color: colors.textPrimary,
    fontWeight: '700',
    textAlign: 'center',
  },
  lifePathBreakdownValue: {
    ...typography.caption,
    color: colors.primary,
    fontWeight: '600',
    textAlign: 'center',
  },
  lifePathCompositeValue: {
    ...typography.caption,
    color: '#FF8C00',
    fontWeight: '700',
    textAlign: 'center',
  },
  heartContainer: {
    width: 60,
    height: 60,
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Detail Cards Section
  detailsContainer: {
    gap: spacing.lg,
  },
  desktopDetailsContainer: {
    flexDirection: 'row',
  },
  modernDetailCard: {
    flex: 1,
    backgroundColor: colors.background,
    borderRadius: borderRadius.xl,
    padding: spacing.xl,
    ...shadows.card,
    borderWidth: 1,
    borderColor: 'rgba(116, 75, 162, 0.1)',
  },
  desktopDetailCard: {
    minWidth: 280,
  },
  detailCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  detailIconContainer: {
    width: 60,
    height: 60,
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.lg,
  },
  detailHeaderText: {
    flex: 1,
  },
  modernDetailTitle: {
    ...typography.heading3,
    color: colors.primary,
    marginBottom: spacing.xs,
  },
  detailSubtitle: {
    ...typography.caption,
    color: colors.textSecondary,
    fontStyle: 'italic',
  },
  progressSection: {
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  progressContainer: {
    width: '100%',
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.primary + '20',
    overflow: 'hidden',
    marginBottom: spacing.sm,
  },
  progressBar: {
    height: '100%',
    borderRadius: 4,
  },
  modernScoreLabel: {
    ...typography.bodySmall,
    fontWeight: '700',
  },
  modernDetailDescription: {
    ...typography.bodySmall,
    color: colors.textSecondary,
    lineHeight: 20,
    textAlign: 'center',
  },
});
